import React, { useEffect, useState } from 'react';
import { Card } from 'react-bootstrap';

import { usePokCore } from '../../../common/hooks/usePokCore';
import { GetGroupedRecommendationsDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import { RecommendedPublisherSearch } from '../../components/RecommendedPublisher/RecommendedPublisherSearch';
import { RecommendedPublisherModalEditor } from '../../components/RecommendedPublisher/RecommendedPublisherModalEditor';
import { RecommendedPublisherAddNewModal } from '../../components/RecommendedPublisher/RecommendedPublisherAddNewModal';
import { useTranslation } from '../../../common/hooks/useTranslation';
import { useNotifications } from '../../../common/hooks/useNotifications';
import { RecommendedPublisherScopeEnum } from '../../../common/pokCore/validation/enums';
import Button from '../../../common/components/Button/Button';

interface SelectedRecommendation {
  profileId: string;
  profileName?: string;
  scopeType: RecommendedPublisherScopeEnum;
  companyId?: string;
  purchaserId?: string;
}

export const RecommendedPublisherView: React.FC = () => {
  const pok = usePokCore();
  const notifications = useNotifications();
  const [recommendations, setRecommendations] = useState<
    GetGroupedRecommendationsDto[]
  >([]);
  const [selectedRecommendation, setSelectedRecommendation] =
    useState<SelectedRecommendation | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showNewModal, setShowNewModal] = useState(false);
  const [refresh, setRefresh] = useState<boolean>(false);
  const [waiting, setWaiting] = useState<boolean>(false);
  const { t, tk } = useTranslation('administrationLng');

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedRecommendation(null);
  };

  const handleCloseNewModal = () => {
    setShowNewModal(false);
  };

  const handleCompleted = () => {
    setRefresh(!refresh);
  };

  const handleAddNew = () => {
    setShowNewModal(true);
  };

  const handleNewProceed = (params: {
    profileId: string;
    profileName: string;
    scopeType: RecommendedPublisherScopeEnum;
    companyId?: string;
    companyName?: string;
    purchaserId?: string;
    purchaserName?: string;
  }) => {
    setShowNewModal(false);
    setSelectedRecommendation({
      profileId: params.profileId,
      profileName: params.profileName,
      scopeType: params.scopeType,
      companyId: params.companyId,
      purchaserId: params.purchaserId,
    });
    setShowModal(true);
  };

  useEffect(() => {
    pok.recommendedPublishers
      .getGroupedRecommendations()
      .then(setRecommendations)
      .catch(errorResponse => {
        notifications.caughtError(errorResponse);
      });
  }, [pok.recommendedPublishers, notifications, refresh]);

  const handleClick = async (key: string) => {
    const row = recommendations?.find(r => r.id === key);

    if (row) {
      setSelectedRecommendation({
        profileId: row.profileId,
        profileName: row.profileName,
        scopeType: row.scopeType as RecommendedPublisherScopeEnum,
        companyId: row.companyId,
        purchaserId: row.clientId,
      });
      setShowModal(true);
    }
  };

  const exportToXls = async () => {
    setWaiting(true);
    const currentDate = new Date().toISOString().split('T')[0];
    pok.reports
      .reportRecommendedPublishersRawData(
        `${t(tk.recommendedPublisher.recommendedPublishersFileName)}_${currentDate}.xlsx`,
      )
      .catch(errorResponse => {
        notifications.caughtError(errorResponse);
      })
      .finally(() => {
        setWaiting(false);
      });
  };

  return (
    <Card className="main-card">
      <Card.Body className="m-3">
        <Card.Title className="pb-3">
          {t(tk.recommendedPublisher.recommendedPublishers)}
        </Card.Title>
        <Button
          variant="outline-primary"
          className="mb-3"
          onClick={handleAddNew}
        >
          {t(tk.recommendedPublisher.recommendedPublisherNew)}
        </Button>
        <RecommendedPublisherSearch
          data={recommendations}
          onRowClick={handleClick}
          refresh={refresh}
        />
        <RecommendedPublisherAddNewModal
          show={showNewModal}
          handleClose={handleCloseNewModal}
          onProceed={handleNewProceed}
        />
        {selectedRecommendation && showModal && (
          <RecommendedPublisherModalEditor
            profileId={selectedRecommendation.profileId}
            profileName={selectedRecommendation.profileName}
            scopeType={selectedRecommendation.scopeType}
            companyId={selectedRecommendation.companyId}
            purchaserId={selectedRecommendation.purchaserId}
            show={showModal}
            handleClose={handleCloseModal}
            onCompleted={handleCompleted}
          />
        )}
        <Button
          variant="outline-secondary"
          onClick={exportToXls}
          isLoading={waiting}
        >
          {t(tk.recommendedPublisher.exportFullList)}
        </Button>
      </Card.Body>
    </Card>
  );
};
